import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { setSessionToken } from '~/features/auth'
import { LinkButton } from '~/features/navigation'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import {
  Alert,
  Button,
  Form,
  FormErrorAlert,
  FormFieldInput,
  Icon,
} from '@hap/components'

export const Route = createFileRoute('/_public/signup/organization')({
  validateSearch: z.object({
    token: z.string(),
    email: z.string().email().optional(),
  }),
  component: RouteComponent,
})

const formSchema = z.object({
  organizationName: z
    .string()
    .min(1, { message: "Please enter your company or team's name" })
    .max(100, { message: 'Your organization name must be less than 100 characters' }),
  alias: z.string().optional(),
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [showAliasField, setShowAliasField] = useState(false)
  const [_, setAliasConflictError] = useState<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)

  const createMutation = useMutation(trpc.signup.createOrganization.mutationOptions())
  const resendMutation = useMutation(trpc.signup.sendCode.mutationOptions())

  const form = useForm({
    values: {
      organizationName: '',
      alias: '',
    },
    resolver: zodResolver(formSchema),
  })

  const watchedAlias = form.watch('alias')

  const normalizedAlias = useMemo(
    () => normalizeAlias(watchedAlias || ''),
    [watchedAlias],
  )

  const shouldCheckAlias =
    showAliasField &&
    normalizedAlias.length >= 3 &&
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedAlias)

  const { data: aliasCheck } = useQuery(
    trpc.signup.checkAlias.queryOptions(
      { alias: normalizedAlias },
      {
        enabled: shouldCheckAlias,
      },
    ),
  )

  const { data: sessionVerification, isLoading: isVerifyingSession } = useQuery(
    trpc.signup.verifySession.queryOptions({ signupToken: search.token }),
  )

  const verifiedEmail = sessionVerification?.email || search.email || null

  useEffect(() => {
    if (!sessionVerification) return
    setSessionExpired(!sessionVerification.valid)
  }, [sessionVerification])

  useEffect(() => {
    if (aliasCheck?.available) setAliasConflictError(null)
  }, [aliasCheck?.available])

  const handleSubmit = form.handleSubmit(async (values) => {
    if (sessionExpired) return

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const alias = showAliasField
      ? normalizeAlias(values.alias || '')
      : normalizeAlias(values.organizationName)

    if (showAliasField && alias.length < 3) {
      form.setError('alias', {
        type: 'manual',
        message: 'URL must be at least 3 characters',
      })
      return
    }

    if (showAliasField && alias.length > 60) {
      form.setError('alias', {
        type: 'manual',
        message: 'URL must be less than 60 characters',
      })
      return
    }

    if (showAliasField && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(alias)) {
      form.setError('alias', {
        type: 'manual',
        message: 'Use lowercase letters, numbers, and hyphens only',
      })
      return
    }

    await createMutation
      .mutateAsync({
        signupToken: search.token,
        name: values.organizationName,
        alias,
        timezone,
      })
      .then((response) => {
        setSessionToken(response.token)
        navigate({
          to: '/org/$orgId/$',
          params: { orgId: response.legacyOrganizationId },
          reloadDocument: true,
          replace: true,
        })
      })
      .catch((error) => {
        if (error?.data?.code === 'UNAUTHORIZED') {
          setSessionExpired(true)
          return
        }

        if (error?.data?.code === 'CONFLICT') {
          setShowAliasField(true)
          form.setValue('alias', alias, { shouldValidate: true })
          form.setError('alias', {
            message: 'That URL is taken. Please choose another one.',
          })
          return
        }

        form.setError('organizationName', {
          message: error?.message || 'There was a problem creating your organization.',
        })
      })
  })

  const handleResendCode = async () => {
    if (!verifiedEmail) return

    setResendError(null)
    await resendMutation
      .mutateAsync({ email: verifiedEmail.toLowerCase() })
      .then(() => {
        navigate({ to: '/signup/code', search: { email: verifiedEmail.toLowerCase() } })
      })
      .catch(() => {
        setResendError('Unable to send a new code right now. Please try again.')
      })
  }

  return (
    <PublicTemplate width={640}>
      <section className="bg-accent grid gap-8 p-20">
        {isVerifyingSession ? (
          <h1 className="text-center text-4xl font-bold">
            Checking your signup session...
          </h1>
        ) : sessionExpired ? (
          <div className="grid gap-4">
            <h1 className="text-center text-4xl font-bold">
              Your signup session expired.
            </h1>
            {verifiedEmail && (
              <>
                <Button
                  type="button"
                  size="lg"
                  loading={resendMutation.isPending}
                  loadingLabel="Sending code..."
                  onClick={handleResendCode}
                >
                  Send a new code
                </Button>
                <p className="text-accent-foreground text-sm">
                  We&apos;ll send a fresh 6-digit code to <strong>{verifiedEmail}</strong>
                  .
                </p>
              </>
            )}
            {resendError && <Alert variant="warn">{resendError}</Alert>}
            <LinkButton to="/signup" variant="ghost" size="sm">
              Back to signup
            </LinkButton>
          </div>
        ) : (
          <>
            <h1 className="mb-10 text-center text-5xl font-bold">
              What&apos;s the name of your company or team?
            </h1>

            <Form {...form}>
              <form onSubmit={handleSubmit} className="grid gap-4">
                <FormErrorAlert />

                <FormFieldInput
                  autoFocus
                  name="organizationName"
                  control={form.control}
                  placeholder="e.g. ABC Heating & Cooling"
                  type="text"
                  size="lg"
                  autoComplete="off"
                />

                {showAliasField &&
                  shouldCheckAlias &&
                  aliasCheck &&
                  !aliasCheck.available && (
                    <div className="bg-earth-yellow/50 mt-3 mb-2 rounded-md p-2">
                      <span>This organization URL is unavailable.</span>
                      {aliasCheck.suggestions.length > 0 && (
                        <>
                          {' '}
                          Try:{' '}
                          {aliasCheck.suggestions.map((suggestion) => (
                            <span
                              key={suggestion}
                              className="cursor-pointer underline"
                              onClick={() => {
                                form.setValue('alias', suggestion, {
                                  shouldValidate: true,
                                })
                              }}
                            >
                              {suggestion}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  )}

                {showAliasField && (
                  <FormFieldInput
                    name="alias"
                    control={form.control}
                    label="Organization URL"
                    placeholder="e.g. abc-heating-and-cooling"
                    type="text"
                    size="lg"
                    autoComplete="off"
                    suffix={
                      aliasCheck?.available ? (
                        <Icon
                          name="check-circle"
                          weight="fill"
                          className="fill-primary mr-2 size-7"
                        />
                      ) : undefined
                    }
                  />
                )}

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  loading={form.formState.isSubmitting || createMutation.isPending}
                  loadingLabel="Creating organization..."
                >
                  Next
                </Button>

                <p className="text-accent-foreground text-center">
                  By continuing, you&apos;re agreeing to our{' '}
                  <a
                    href="https://happitu.com/legal/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2"
                  >
                    Customer Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://happitu.com/legal/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            </Form>
          </>
        )}
      </section>
    </PublicTemplate>
  )
}

function normalizeAlias(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}
